// Lib
import React, { CSSProperties, RefObject } from 'react';
import { useSelector } from 'react-redux';
import { cx } from '@milanote/roma/utils';

// Utils
import { stopPropagationOnly } from '../../../../../utils/domUtil';

// Selectors
import { currentBoardHasMediaDownloadAccessSelector } from '../../../../../utils/permissions/elementPermissionsSelector';
import { isPresentationModeEnabledSelector } from '../../../../../workspace/presentation/presentationSelector';

// Components
import ImageModalV2PrimaryTools from './ImageModalV2PrimaryTools';
import ImageModalV2ToolbarSurface from './ImageModalV2ToolbarSurface';

// Types
import { ImageModalV2Mode } from '../hooks/useImageModalV2Mode';
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

// Constants
import { IMAGE_MODAL_SECONDARY_TOOLBAR_ID } from '../../imageModalConstants';
import { IMAGE_MODAL_V2_BOTTOM_SURFACE, type ImageModalV2BottomSurface } from './imageModalV2BottomSurface';
import { PRIMARY_TOOLBAR_BOTTOM_OFFSET } from '../hooks/useImageModalV2Layout';

// Styles
import './ImageModalV2Toolbar.scss';

type ImageModalV2ToolbarProps = {
    element: ImMNElement;
    mode: ImageModalV2Mode;
    isEditable: boolean;
    canNavigate: boolean;
    navigateToPreviousImage: () => void;
    navigateToNextImage: () => void;
    setMode: (mode: ImageModalV2Mode) => void;
    bottomSurface: ImageModalV2BottomSurface | null;
    isVisible?: boolean;
    style?: CSSProperties;
    primaryToolbarRef?: RefObject<HTMLDivElement>;
    onPrimaryToolbarExitComplete: () => void;
    onSecondaryToolbarExitComplete: () => void;
};

const ImageModalV2Toolbar: React.FC<ImageModalV2ToolbarProps> = ({
    element,
    mode,
    isEditable,
    canNavigate,
    navigateToPreviousImage,
    navigateToNextImage,
    setMode,
    bottomSurface,
    isVisible = true,
    style,
    primaryToolbarRef,
    onPrimaryToolbarExitComplete,
    onSecondaryToolbarExitComplete,
}) => {
    const showDownloadOption = useSelector(currentBoardHasMediaDownloadAccessSelector);
    const isPresentationMode = useSelector(isPresentationModeEnabledSelector);
    const showPrimary = bottomSurface === IMAGE_MODAL_V2_BOTTOM_SURFACE.PRIMARY_TOOLBAR;
    const showSecondary = bottomSurface === IMAGE_MODAL_V2_BOTTOM_SURFACE.SECONDARY_TOOLBAR;
    const secondaryBottom = typeof style?.bottom === 'number' ? style.bottom : 0;

    return (
        <div
            data-bottom-surface={bottomSurface}
            className={cx(
                'ImageModalV2Toolbar absolute right-0 bottom-0 left-0 z-[2] w-full pointer-events-none transition-opacity duration-100 ease-out',
                !isVisible && 'opacity-0 [&_*]:!pointer-events-none',
            )}
            style={{ height: `calc(3rem + ${secondaryBottom}px)` }}
        >
            <div className={cx('ImageModalV2Toolbar-animation-container', 'relative w-full')}>
                <ImageModalV2ToolbarSurface
                    isOpen={showPrimary}
                    motion="primary"
                    skipInitialEnter
                    onExitComplete={onPrimaryToolbarExitComplete}
                    className="ImageModalV2Toolbar-primary !absolute !top-auto left-1/2 z-[1] flex h-10 w-full -translate-x-1/2 items-center justify-center pointer-events-none"
                    style={{ bottom: PRIMARY_TOOLBAR_BOTTOM_OFFSET }}
                >
                    <div ref={primaryToolbarRef} className="flex h-full w-full items-center justify-center">
                        <ImageModalV2PrimaryTools
                            element={element}
                            mode={mode}
                            isEditable={isEditable}
                            showDownloadOption={showDownloadOption}
                            isPresentationMode={isPresentationMode}
                            canNavigate={canNavigate}
                            navigateToPreviousImage={navigateToPreviousImage}
                            navigateToNextImage={navigateToNextImage}
                            setMode={setMode}
                        />
                    </div>
                </ImageModalV2ToolbarSurface>
                <ImageModalV2ToolbarSurface
                    isOpen={showSecondary}
                    motion="secondary"
                    onExitComplete={onSecondaryToolbarExitComplete}
                    className="ImageModalV2Toolbar-secondary !absolute !top-auto left-1/2 z-0 flex h-12 w-full -translate-x-1/2 items-center justify-center pointer-events-none"
                    style={{ bottom: secondaryBottom }}
                >
                    <div
                        id={IMAGE_MODAL_SECONDARY_TOOLBAR_ID}
                        className="ImageModalV2SecondaryTools image-secondary-tools"
                        onMouseDown={stopPropagationOnly}
                        onClick={stopPropagationOnly}
                    />
                </ImageModalV2ToolbarSurface>
            </div>
        </div>
    );
};

export default ImageModalV2Toolbar;
